import { Layer } from "@carbon/react";
import React from "react";

import { ProgressStepModal, useProgressStepModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { getRulesQuery } from "src/graphql/anti-fraud/queries/rules/GetRulesQuery.ts";
import { createRuleMutation } from "src/graphql/anti-fraud/queries/rules/mutations/CreateRuleMutation.ts";
import { usePerformMutation } from "src/graphql/utils";
import { useT } from "src/internationalization";
import { mapToApiOperatorCode } from "src/mappers/operator.mappers";
import { Rule } from "src/types/rule";

import { useDetailsForm } from "../DetailsForm";

import { CreateRuleModalStep1 } from "./CreateRuleModalStep1";
import { CreateRuleModalStep2 } from "./CreateRuleModalStep2";
import { useRuleDefinition } from "./useRuleDefinition";

export type DetailsFormData = {
  code: string;
  name: string;
  description: string;
};

type CreateRuleModalProps = {
  onClose: () => void;
  caseTypeCode: string;
  rules: Rule[];
};

export const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  onClose,
  caseTypeCode,
  rules,
}) => {
  const t = useT();
  const { showSuccessToast } = useToast();

  const { methods: constructionMethods } = useRuleDefinition();

  const { methods: detailsMethods } = useDetailsForm({ rules });

  const {
    performMutation: createRule,
    loading,
    error,
  } = usePerformMutation(createRuleMutation, {
    onSuccess: () => {
      showSuccessToast(t("page.rules.modal.create.toast.success.message"));
      onClose();
    },
    mutationOption: {
      refetchQueries: [getRulesQuery],
      awaitRefetchQueries: true,
    },
  });

  const onSubmit = async (detailsData: DetailsFormData) => {
    const constructionData = constructionMethods.getValues();

    const singleValueConditions = constructionData.criteria
      .filter((criterion) => criterion.operator?.valueType !== "argumentList")
      .map((criterion) => ({
        propertyCode: criterion.property?.code || "",
        operatorCode: criterion.operator?.code ? mapToApiOperatorCode(criterion.operator.code) : "",
        value: criterion.value,
      }));

    const multiValueConditions = constructionData.criteria
      .filter((criterion) => criterion.operator?.valueType === "argumentList")
      .map((criterion) => ({
        propertyCode: criterion.property?.code || "",
        operatorCode: criterion.operator?.code ? mapToApiOperatorCode(criterion.operator.code) : "",
        values: criterion.value ? criterion.value.split(",") : [],
      }));

    await createRule({
      code: detailsData.code,
      name: detailsData.name,
      description: detailsData.description,
      listElementType: constructionData.listElementType!.code,
      caseTypeCode: caseTypeCode,
      identifierPath: {
        elementPath: [constructionData.identifier!.elementCode],
        identifierCode: constructionData.identifier!.identifierCode,
        origin:
          constructionData.identifier?.clientType === "customer" ? "CustomerOrigin" : "CaseOrigin",
      },
      blackListElementConditions: singleValueConditions,
      multiValueConditions: multiValueConditions,
    });
  };

  const { control: stepsControl, modalProps } = useProgressStepModal([
    {
      title: t("page.rules.modal.create.step.construction.title"),
      type: "construction",
      invalid: !constructionMethods.formState.isValid && constructionMethods.formState.isSubmitted,
      disabled: false,
      submit: constructionMethods.handleSubmit(() => {
        stepsControl.goNextStep();
      }),
      focus: "#identifier",
    },
    {
      title: t("page.rules.modal.create.step.details.title"),
      type: "details",
      invalid: !detailsMethods.formState.isValid && detailsMethods.formState.isSubmitted,
      disabled: false,
      submit: detailsMethods.handleSubmit(onSubmit),
      focus: "#name",
    },
  ]);

  return (
    <ProgressStepModal
      loading={loading}
      kind="vertical"
      error={mapToModalError(error)}
      initialFocus="#identifier"
      title={t("page.rules.modal.create.watchlist.title")}
      confirmButtonText={t("general.actions.create")}
      onClose={onClose}
      {...modalProps}
    >
      <Layer level={1} className="flex flex-col h-full min-h-[540px] bg-layer01">
        <CreateRuleModalStep1
          caseTypeCode={caseTypeCode}
          methods={constructionMethods}
          hidden={!stepsControl.isStep("construction")}
        />

        <CreateRuleModalStep2 hidden={!stepsControl.isStep("details")} methods={detailsMethods} />
      </Layer>
    </ProgressStepModal>
  );
};
